import { FC } from "react";

import ContentAvatar, {
  LIBRARY_ITEM_TYPE_TO_CONTENT_TYPE,
  MediaThumbnail,
} from "@src/features/contentLibrary/ContentAvatar";
import {
  getThumbnailUrl,
  isVideoMedia,
} from "@src/features/contentDetail/utils/media";
import { useOpenGraphDataQuery } from "@src/features/contentDetail/ui/MediaPreview.generated";
import { OgData } from "@src/features/libraryItem/course/OpenGraphLinkPreview";
import { getLinkType, isMux, LinkType } from "@src/lib/utils/files";
import { cn } from "@src/lib/utils/cn";
import ClickableIframe from "@src/lib/ui/ClickableIframe";
import PdfChipPreview from "@src/features/messaging/new/chats/PdfChipPreview";
import { NewMessagingPostFragment } from "./SentPosts.generated";
import { claimClick } from "./PostCard";

export type PostMediaTileMedia = NonNullable<
  NewMessagingPostFragment["messageBlastMedias"]
>[number];

type Props = {
  attachment: PostMediaTileMedia;
  onClick?: () => void;
  videoIconClassName?: string;
  chip?: boolean;
};

const OG_LINK_TYPES = [LinkType.LINK, LinkType.WEBSITE, LinkType.HOSTED_VIDEO];

export const getTileUrl = (
  media: NonNullable<PostMediaTileMedia["media"]>,
): string => {
  if (isVideoMedia(media) && isMux(media.mediaUrls.en)) {
    return media.mediaUrls.en;
  }
  const url = getThumbnailUrl(media);
  if (media.thumbnailImageUrl && getLinkType(url) === LinkType.PDF) {
    return media.thumbnailImageUrl;
  }
  return url;
};

const PostMediaTile: FC<Props> = ({
  attachment,
  onClick,
  videoIconClassName = "size-12",
  chip,
}) => {
  const { media, libraryItem } = attachment;

  const url = media && !libraryItem ? getTileUrl(media) : "";
  const isPendingPdf =
    !!media &&
    !libraryItem &&
    !media.thumbnailImageUrl &&
    getLinkType(url) === LinkType.PDF;
  const ogQuery = useOpenGraphDataQuery({
    variables: { url },
    skip: !url || isPendingPdf || !OG_LINK_TYPES.includes(getLinkType(url)),
  });

  const content = libraryItem ? (
    <ContentAvatar
      className="size-full rounded-none object-contain"
      style={{ borderRadius: 0 }}
      defaultBackgroundHashKey={libraryItem.id}
      contentType={LIBRARY_ITEM_TYPE_TO_CONTENT_TYPE[libraryItem.type]}
      coverImage={libraryItem.coverImage}
    />
  ) : media && isPendingPdf && chip ? (
    <PdfChipPreview mediaUrl={url} />
  ) : media && isPendingPdf ? (
    <div className="size-full overflow-hidden bg-gray-2">
      <ClickableIframe
        className="size-full border-0 outline-none"
        title="PDF"
        src={`${url}#toolbar=0&navpanes=0&scrollbar=0`}
        onClick={onClick}
      />
    </div>
  ) : media ? (
    <MediaThumbnail
      className="size-full rounded-none object-cover"
      style={{ borderRadius: 0 }}
      url={url}
      ogData={ogQuery.data?.OpenGraphData as OgData | undefined}
      videoIconClassName={videoIconClassName}
    />
  ) : null;

  if (!content) return null;

  return (
    <div
      className={cn("size-full", onClick && "cursor-pointer")}
      onClick={onClick && claimClick(onClick)}
    >
      {content}
    </div>
  );
};

export default PostMediaTile;
